import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, LogOut, User, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { formatINR, resolveImage } from '@/lib/format';

const STATUS_COLORS = {
    paid: 'bg-blue-100 text-blue-700',
    processing: 'bg-amber-100 text-amber-700',
    shipped: 'bg-purple-100 text-purple-700',
    delivered: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    created: 'bg-gray-100 text-gray-600',
};

export default function Account() {
    const { user, loading: authLoading, logout } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) { setLoading(false); return; }
        api.get('/orders').then(({ data }) => setOrders(data)).catch(() => setOrders([])).finally(() => setLoading(false));
    }, [user]);

    if (authLoading) return <div className="py-32 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[var(--brand-blue)]" /></div>;

    if (!user) {
        return (
            <div className="max-w-md mx-auto px-4 py-24 text-center">
                <div className="h-16 w-16 rounded-full bg-[var(--surface-2)] grid place-items-center mx-auto"><User className="h-7 w-7 text-[var(--ink-muted)]" /></div>
                <h1 className="font-display text-2xl font-bold mt-5">Please log in</h1>
                <p className="text-[var(--ink-muted)] mt-2">Log in to view your account and orders.</p>
                <Link to="/login" className="btn-blue mt-6 inline-flex h-12 px-7 rounded-xl font-semibold items-center">Log in</Link>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-2xl royal-panel grid place-items-center text-white font-display text-xl font-bold">{user.name?.charAt(0)?.toUpperCase()}</div>
                    <div><h1 className="font-display text-2xl font-bold">{user.name}</h1><p className="text-[var(--ink-muted)] text-sm">{user.email}</p></div>
                </div>
                <button onClick={logout} className="h-11 px-5 rounded-xl border border-border bg-white hover:bg-[var(--surface-2)] font-medium inline-flex items-center gap-2 transition-colors"><LogOut className="h-4 w-4" /> Log out</button>
            </div>

            <div className="mt-10">
                <h2 className="font-display text-xl font-bold flex items-center gap-2"><Package className="h-5 w-5" /> Order history</h2>
                {loading ? (
                    <div className="py-16 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-[var(--brand-blue)]" /></div>
                ) : orders.length === 0 ? (
                    <div className="mt-6 rounded-2xl border border-dashed border-border p-12 text-center">
                        <p className="font-medium">No orders yet</p>
                        <p className="text-sm text-[var(--ink-muted)] mt-1">When you place an order it will show up here.</p>
                        <Link to="/shop" className="btn-blue mt-5 inline-flex h-11 px-6 rounded-xl font-semibold items-center">Start shopping</Link>
                    </div>
                ) : (
                    <div className="mt-6 space-y-4">
                        {orders.map((o) => (
                            <div key={o.id} className="rounded-2xl border border-border bg-white overflow-hidden">
                                <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3 flex-wrap bg-[var(--surface-2)]">
                                    <div className="flex items-center gap-3"><span className="font-display font-semibold">{o.order_number}</span><span className="text-xs text-[var(--ink-muted)]">{(o.created_at || '').slice(0, 10)}</span></div>
                                    <div className="flex items-center gap-3"><span className={`text-xs px-2.5 py-1 rounded-full font-semibold uppercase ${STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-600'}`}>{o.status}</span><span className="font-display font-bold">{formatINR(o.total)}</span></div>
                                </div>
                                <div className="p-5 flex flex-wrap gap-4">
                                    {o.items.map((it) => (
                                        <div key={it.product_id} className="flex items-center gap-2">
                                            <img src={resolveImage(it.image)} alt={it.name} loading="lazy" decoding="async" className="h-12 w-12 rounded-lg object-contain bg-[var(--surface-2)] p-1" />
                                            <div><p className="text-sm font-medium">{it.name}</p><p className="text-xs text-[var(--ink-muted)]">Qty {it.qty}</p></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
